import { createContext, useContext, useEffect, useState } from "react";
import {
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db, app } from "../../config/firebase";

const auth = getAuth(app);
const AdminAuthContext = createContext(null);
const VALID_ROLES = new Set(["viewer", "admin", "super_admin"]);

function normalizeAdmin(user, data) {
  const role = data.role || "super_admin";
  if (data.active === false || !VALID_ROLES.has(role)) return null;
  return { uid: user.uid, email: user.email, ...data, role, active: true };
}

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null); // { uid, email, name }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stopProfile = () => {};
    const stopAuth = onAuthStateChanged(auth, async (user) => {
      stopProfile();
      setLoading(true);
      if (!user) {
        setAdmin(null);
        setLoading(false);
        return;
      }
      stopProfile = onSnapshot(doc(db, "admins", user.uid), async (snap) => {
        try {
        const profile = snap.exists() ? normalizeAdmin(user, snap.data()) : null;
        if (!profile) {
          setError("not-authorized");
          setAdmin(null);
          await signOut(auth);
        } else {
          setAdmin(profile);
          setError(null);
        }
        } catch (_) {
          setAdmin(null);
          setError("admin-profile-unavailable");
          await signOut(auth);
        }
        setLoading(false);
      }, async () => {
        setAdmin(null);
        setError("admin-profile-unavailable");
        setLoading(false);
        await signOut(auth);
      });
    });
    return () => {
      stopProfile();
      stopAuth();
    };
  }, []);

  async function login(email, password) {
    setError(null);
    try {
      await setPersistence(auth, browserLocalPersistence);
      const credential = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password,
      );
      const snap = await getDoc(doc(db, "admins", credential.user.uid));
      const profile = snap.exists()
        ? normalizeAdmin(credential.user, snap.data())
        : null;
      if (!profile) {
        await signOut(auth);
        setError("not-authorized");
        return false;
      }
      setAdmin(profile);
      return true;
    } catch (_) {
      setError("invalid-credentials");
      return false;
    }
  }

  async function logout() {
    await signOut(auth);
  }

  async function resetPassword(email) {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      return true;
    } catch (_) {
      setError("password-reset-failed");
      return false;
    }
  }

  return (
    <AdminAuthContext.Provider
      value={{
        admin,
        loading,
        error,
        login,
        logout,
        resetPassword,
        canManage: admin?.role === "admin" || admin?.role === "super_admin",
        isSuperAdmin: admin?.role === "super_admin",
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  }
  return context;
}
